import { useEffect, useMemo } from 'react';

import type { PaymentFlowErrorModalState, UsePaymentFlowErrorModalParams } from './types';

const isPaymentRoute = (pathname: string, baseUrl: string): boolean =>
	pathname.includes(`${baseUrl}/payment/failed`) ||
	pathname.includes(`${baseUrl}/payment/successfull`) ||
	pathname === `${baseUrl}/payment`;

/**
 * Возвращает состояние payment error modal для текущего payment-маршрута.
 *
 * Нужен на уровне Checkout/Checkin: ошибки могут возникнуть на `{baseUrl}/payment`,
 * когда `PaymentResultFlow` не смонтирован.
 *
 * /payment/failed - failedFlow
 * /payment/successfull - successFlow
 * /payment - paymentFlow
 *
 * @returns `error` + колбеки или `null`, если маршрут не payment/* или ошибки нет
 */
export const usePaymentFlowErrorModal = ({
	pathname,
	baseUrl,
	failedFlow,
	successFlow,
	paymentFlow
}: UsePaymentFlowErrorModalParams): PaymentFlowErrorModalState | null => {
	const {
		error: paymentFailedFlowError,
		clearError: clearPaymentFailedFlowError,
		onErrorConfirm: onPaymentFailedFlowErrorConfirm,
		onErrorClose: onPaymentFailedFlowErrorClose,
		onErrorCancel: onPaymentFailedFlowErrorCancel
	} = failedFlow;

	const {
		error: paymentSuccessFlowError,
		clearError: clearPaymentSuccessFlowError,
		onErrorConfirm: onPaymentSuccessFlowErrorConfirm,
		onErrorClose: onPaymentSuccessFlowErrorClose,
		onErrorCancel: onPaymentSuccessFlowErrorCancel
	} = successFlow;

	const {
		error: paymentPageFlowError,
		clearError: clearPaymentPageFlowError,
		onErrorConfirm: onPaymentPageFlowErrorConfirm,
		onErrorClose: onPaymentPageFlowErrorClose,
		onErrorCancel: onPaymentPageFlowErrorCancel
	} = paymentFlow;

	/*
	 * error хранится в хуках Checkout/Checkin и не сбрасывается при уходе с payment-маршрута.
	 * Чистим при смене pathname и при опоздавшем setError, чтобы модалка не всплывала при возврате на страницы.
	 */
	useEffect(() => {
		if (isPaymentRoute(pathname, baseUrl)) {
			return;
		}

		if (paymentFailedFlowError) {
			clearPaymentFailedFlowError?.();
		}

		if (paymentSuccessFlowError) {
			clearPaymentSuccessFlowError?.();
		}

		if (paymentPageFlowError) {
			clearPaymentPageFlowError?.();
		}
	}, [
		pathname,
		baseUrl,
		paymentFailedFlowError,
		paymentSuccessFlowError,
		paymentPageFlowError,
		clearPaymentFailedFlowError,
		clearPaymentSuccessFlowError,
		clearPaymentPageFlowError
	]);

	return useMemo(() => {
		if (pathname.includes(`${baseUrl}/payment/failed`)) {
			if (!paymentFailedFlowError) {
				return null;
			}

			return {
				error: paymentFailedFlowError,
				onConfirm: onPaymentFailedFlowErrorConfirm,
				onClose: onPaymentFailedFlowErrorClose,
				onCancel: onPaymentFailedFlowErrorCancel
			};
		}

		if (pathname.includes(`${baseUrl}/payment/successfull`)) {
			if (!paymentSuccessFlowError) {
				return null;
			}

			return {
				error: paymentSuccessFlowError,
				onConfirm: onPaymentSuccessFlowErrorConfirm,
				onClose: onPaymentSuccessFlowErrorClose,
				onCancel: onPaymentSuccessFlowErrorCancel
			};
		}

		if (pathname === `${baseUrl}/payment`) {
			if (!paymentPageFlowError) {
				return null;
			}

			return {
				error: paymentPageFlowError,
				onConfirm: onPaymentPageFlowErrorConfirm,
				onClose: onPaymentPageFlowErrorClose,
				onCancel: onPaymentPageFlowErrorCancel
			};
		}

		return null;
	}, [
		pathname,
		baseUrl,
		paymentFailedFlowError,
		paymentSuccessFlowError,
		paymentPageFlowError,
		onPaymentFailedFlowErrorConfirm,
		onPaymentSuccessFlowErrorConfirm,
		onPaymentPageFlowErrorConfirm,
		onPaymentFailedFlowErrorClose,
		onPaymentSuccessFlowErrorClose,
		onPaymentPageFlowErrorClose,
		onPaymentFailedFlowErrorCancel,
		onPaymentSuccessFlowErrorCancel,
		onPaymentPageFlowErrorCancel
	]);
};
